import { useState } from "react";
import {
  setLabOrderRequisitionRef,
  UnauthorizedError,
} from "@/lib/clinician-api";
import {
  normalizedRequisitionRef,
  requisitionRefError,
} from "@/lib/requisition-ref";
import { useReadOnly } from "@/components/SupportView";
import { RequisitionRefInput } from "@/components/WebdocControlStep";
import { LAB_LID_LABEL, LAB_RID_LABEL } from "@/lib/labportalen";

// TES-442 — the remiss number on a lab order, editable at any time (the
// doctor may not have it at hand when she ticks the remiss). Every change is
// audited by the API. Shows the requisition id learned from LabPortalen too,
// read-only, once a result has been matched.
//
// Proposed copy, awaiting Emelie.
const SAVE_ERROR: Record<string, string> = {
  requisition_ref_in_use:
    "Remissnumret finns redan på en annan beställning. Kontrollera numret.",
  validation_failed: "Endast bokstäver, siffror och bindestreck.",
};

export function LabOrderRequisitionRef({
  patientId,
  orderId,
  requisitionRef,
  labPortalenRid,
  labPortalenExternalRid = null,
  locked,
  onUnauthorized,
}: {
  patientId: string;
  orderId: string;
  requisitionRef: string | null;
  /** The LID: Requisition@RequisitionID, the performing lab's number. */
  labPortalenRid: string | null;
  /** The RID: Requisition@ExternalRequisitionID (TES-1341). */
  labPortalenExternalRid?: string | null;
  /** A cancelled order: shown, not editable. */
  locked: boolean;
  onUnauthorized: () => void;
}) {
  // A support view (admin) reads only.
  const readOnly = useReadOnly() || locked;
  const [saved, setSaved] = useState(requisitionRef);
  const [draft, setDraft] = useState(requisitionRef ?? "");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (requisitionRefError(draft)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await setLabOrderRequisitionRef(
        patientId,
        orderId,
        normalizedRequisitionRef(draft),
      );
      setSaved(res.requisitionRef);
      setDraft(res.requisitionRef ?? "");
      setEditing(false);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      const code = e instanceof Error ? e.message : "";
      setError(SAVE_ERROR[code] ?? "Kunde inte spara remissnumret.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-xs flex flex-col gap-xs text-xs text-textSecondary">
      {!editing ? (
        <p className="flex flex-wrap items-center gap-sm">
          <span>
            Remissnummer:{" "}
            <span className="text-textPrimary">{saved ?? "—"}</span>
          </span>
          {/* TES-1341 — labPortalenRid holds the LID (the lab's number), not
              the RID; the RID is labPortalenExternalRid. */}
          {labPortalenRid ? (
            <span>
              • {LAB_LID_LABEL}:{" "}
              <span className="text-textPrimary">{labPortalenRid}</span>
            </span>
          ) : null}
          {labPortalenExternalRid ? (
            <span>
              • {LAB_RID_LABEL}:{" "}
              <span className="text-textPrimary">{labPortalenExternalRid}</span>
            </span>
          ) : null}
          {!readOnly ? (
            <button
              type="button"
              className="text-brand underline-offset-2 hover:underline"
              onClick={() => setEditing(true)}
            >
              {saved ? "Ändra" : "Lägg till"}
            </button>
          ) : null}
        </p>
      ) : (
        <form
          className="flex flex-col gap-xs"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <RequisitionRefInput
            id={`lab-order-${orderId}-requisition-ref`}
            value={draft}
            onChange={setDraft}
            disabled={busy}
          />
          <div className="flex gap-sm pl-lg">
            <button
              type="submit"
              disabled={busy || !!requisitionRefError(draft)}
              className="rounded-md border border-brand px-sm py-xs text-xs text-brand hover:bg-brand/5 disabled:opacity-50"
            >
              Spara
            </button>
            <button
              type="button"
              disabled={busy}
              className="px-sm py-xs text-xs text-textSecondary hover:underline"
              onClick={() => {
                setDraft(saved ?? "");
                setEditing(false);
                setError(null);
              }}
            >
              Avbryt
            </button>
          </div>
          {error ? (
            <p role="alert" className="pl-lg text-xs text-danger">
              {error}
            </p>
          ) : null}
        </form>
      )}
    </div>
  );
}
